import { AttributeType } from "@interfaces/attribute.interface";

/** Percentage bounds; enforced while typing so an out-of-range value can't be entered. */
export const MAX_PERCENTAGE = 999;

export type MaskInputVariant =
  | AttributeType.CURRENCY
  | AttributeType.PERCENTAGE
  | AttributeType.MULTIPLE
  | AttributeType.CREDIT
  | AttributeType.NUMBER
  | AttributeType.NUMBER_NOT_NEGATIVE;

export interface IMaskInputVariantConfig {
  placeholder: string;
  /** `initial` renders before the value, `final` right after it. */
  prefixes: { initial?: string; final?: string };
  min?: number;
  max?: number;
  integer?: boolean;
  /** Reject keystrokes that leave min/max; without it the bounds are advisory. */
  enforceMinMax?: boolean;
  /** Scale the read-only value and swap `final` for a mm/bn suffix. */
  scaleWhenReadOnly?: boolean;
  /** Fixed decimals for the read-only value, so 20 reads as "20.0". Editing shows it verbatim. */
  displayDecimals?: number;
  disableCommas?: boolean;
}

/**
 * Mirrors oberon's MASK_INPUT_VARIANTS, with one deliberate difference: percentage is capped at
 * ±999 and enforced, where the CRM declares an unenforced max of 100.
 */
export const MASK_INPUT_VARIANTS: Record<MaskInputVariant, IMaskInputVariantConfig> = {
  [AttributeType.CURRENCY]: {
    placeholder: "00.0",
    prefixes: { initial: "$" },
    scaleWhenReadOnly: true,
  },
  [AttributeType.PERCENTAGE]: {
    placeholder: "00.0",
    prefixes: { final: "%" },
    displayDecimals: 1,
    min: -MAX_PERCENTAGE,
    max: MAX_PERCENTAGE,
    enforceMinMax: true,
  },
  [AttributeType.MULTIPLE]: {
    placeholder: "0.0",
    prefixes: { final: "x" },
  },
  [AttributeType.CREDIT]: {
    placeholder: "00.0",
    prefixes: { final: "%" },
    min: 0,
    disableCommas: true,
  },
  [AttributeType.NUMBER]: {
    placeholder: "00.0",
    prefixes: {},
  },
  [AttributeType.NUMBER_NOT_NEGATIVE]: {
    placeholder: "00",
    prefixes: {},
    min: 0,
    integer: true,
    enforceMinMax: true,
  },
};

export const MASK_INPUT_VARIANT_TYPES = Object.keys(MASK_INPUT_VARIANTS) as MaskInputVariant[];

export function isMaskInputVariant(type: AttributeType): type is MaskInputVariant {
  return MASK_INPUT_VARIANT_TYPES.includes(type as MaskInputVariant);
}
